// @flow

import * as React from 'react';

import { formatPrice } from 'utils/currencyUtils';
import { type Locale } from 'common/types/locale';

type Venue = {
  rent_per_day: number | null,
  sales_guarantee: number | null,
  rent_per_hour: number | null,
  rent_per_person: number | null,
  locale: Locale,
  ...
};

type Props = {|
  venue: Venue
|};

export default function VenuePreviewPrice(props: Props) {
  const pricingPreview = pickVenuePricingPreview(props.venue);

  return pricingPreview ? (
    <div className="b-overlay b-overlay--left b-search-result__overlay">
      <div className="b-search-result__price">
        {`${__('search.venue_preview_price.starting_from_prefix')} `}
        {pricingPreview.formattedPrice}
      </div>
      <div className="b-search-result__price-detail">
        {pricingPreview.legend}
      </div>
    </div>
  ) : null;
}

type PricingPreviewOption = {|
  price: number,
  formattedPrice: string,
  legend: string
|};

function venuePricingPreviewOptions(
  venue: Venue,
  formatPriceWithLocale: number => string
): Array<PricingPreviewOption> {
  return [
    {
      price: venue.rent_per_day || 0,
      formattedPrice: formatPriceWithLocale(venue.rent_per_day || 0),
      legend: __('search.venue_preview_price.rent_per_day')
    },
    {
      price: venue.rent_per_hour || 0,
      formattedPrice: formatPriceWithLocale(venue.rent_per_hour || 0),
      legend: __('search.venue_preview_price.rent_per_hour.hour')
    },
    {
      price: venue.rent_per_person || 0,
      formattedPrice: formatPriceWithLocale(venue.rent_per_person || 0),
      legend: __('search.venue_preview_price.rent_per_person.person')
    },
    {
      price: venue.sales_guarantee || 0,
      formattedPrice: formatPriceWithLocale(venue.sales_guarantee || 0),
      legend: __('search.venue_preview_price.sales_guarantee')
    }
  ];
}

function pickVenuePricingPreview(venue: Venue): ?PricingPreviewOption {
  const pricingPreviewsOptions = venuePricingPreviewOptions(venue, price =>
    formatPrice(price, { precision: 0, locale: venue.locale })
  );

  const firstApplicablePricingPreview = pricingPreviewsOptions.find(
    p => p.price > 0
  );

  return firstApplicablePricingPreview;
}
